import { Tag } from 'magpie-types/tags';
import { mergeLabelValues } from '../../utils';
import {
  AudienceFilterContext,
  IAudienceFilter,
  IAudienceFilterPredicate,
} from './interfaces';
import {
  CrossPortalPredicate,
  CrossTabPredicate,
  ExcludeSelfPredicate,
  MatchingLabelPredicate,
  MatchingTagPredicate,
  SameWindowPredicate,
  TargetAppPredicate,
} from './audience-filter-predicate';

export class AudienceFilter {
  readonly #instance: IAudienceFilter;
  static readonly #predicates: IAudienceFilterPredicate[] = [
    new TargetAppPredicate(),
    new MatchingTagPredicate(),
    new MatchingLabelPredicate(),
    new ExcludeSelfPredicate(),
    new SameWindowPredicate(),
    new CrossTabPredicate(),
    new CrossPortalPredicate(),
  ];

  private constructor(initial: Partial<IAudienceFilter> = {}) {
    this.#instance = {
      tags: new Set(),
      apps: new Set(),
      labels: new Map(),
      excludeSelf: false,
      sameWindow: false,
      allowCrossTab: false,
      allowCrossPortal: false,
      ...initial,
    };
  }
  static builder(): AudienceFilter {
    return new AudienceFilter();
  }

  static from(instance: Partial<IAudienceFilter>) {
    return new AudienceFilter(instance);
  }

  byTag = (tag: Tag): AudienceFilter => {
    this.#instance.tags.add(tag);
    return this;
  };

  byApplication = ({ name }: { name: string }): AudienceFilter => {
    this.#instance.apps.add(name);
    return this;
  };

  byLabel = (key: string, values: string[] | string): AudienceFilter => {
    this.#instance.labels = mergeLabelValues(
      this.#instance.labels,
      key,
      values
    );
    return this;
  };

  sameWindow = (targetOnlySameWindow = true): AudienceFilter => {
    this.#instance.sameWindow = targetOnlySameWindow;
    return this;
  };

  excludeSelf = (excludeSelfFromRecipients = true): AudienceFilter => {
    this.#instance.excludeSelf = excludeSelfFromRecipients;
    return this;
  };

  crossTab = (allowCrossTab = true): AudienceFilter => {
    this.#instance.allowCrossTab = allowCrossTab;
    return this;
  };

  crossPortal = (allowCrossPortal = true): AudienceFilter => {
    this.#instance.allowCrossPortal = allowCrossPortal;
    return this;
  };

  build = () => this.#instance;

  isTargetAudience = (context: AudienceFilterContext) => {
    return AudienceFilter.isTargetAudience(this.#instance, context);
  };

  static isTargetAudience = (
    audience: IAudienceFilter,
    context: AudienceFilterContext
  ): boolean =>
    AudienceFilter.#predicates.every((predicate) =>
      predicate.isTargetAudience(audience, context)
    );
}
